import React from 'react'

import Button from 'components/Form/Button'
import Check from 'components/Form/Check'
import Input from 'components/Form/Input'
import FormCore from 'components/Form/Core'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import useInviteForm from './formController'
import ModalWrapper from 'components/Modal/Wrapper'
import CompanyAutocomplete from './CompanyAutocomplete'
import ContentLockSelector from './ContentLockSelector'
import ContentPresetSelector from './ContentPresetSelector'
import { ThemedComponent } from 'ui/theme'
import useI18n from 'hooks/useI18n'
import {
  AdvancedOptions,
  CompanyHint,
  FormActions,
  FormField,
  FormFieldsGrid,
  ModalBody,
} from './styled'

export default function InviteFormModal({
  editingInvite,
  form,
  saving,
  companyOptions,
  companyLoading,
  companyQuery,
  contentGroups,
  contentGroupsLoading,
  contentGroupsError,
  selectedContentGroupId,
  contentCatalog,
  contentCatalogTypes,
  contentCatalogLoading,
  contentCatalogError,
  contentQuery,
  contentType,
  setContentQuery,
  setContentType,
  setCompanyQuery,
  setForm,
  onContentGroupChange,
  onClose,
  onSave,
}) {
  const { t } = useI18n()
  const { formRef, formItems, initialValues, save } = useInviteForm({ editingInvite, form, onSave, t })
  const selectedCompany = (companyOptions || []).find(option => (
    String(option.id) === String(form.company_id)
  )) || null

  const handleCompanyChange = selected => {
    setForm(current => ({
      ...current,
      company_name: selected?.name || '',
      company_id: selected?.id || '',
      company_selection_changed: true,
    }))
    setCompanyQuery(selected?.name || '')
  }

  return (
    <ModalWrapper
      title={t(editingInvite ? 'staff_essentials_invites_edit_modal' : 'staff_essentials_invites_create_modal')}
      text={t('lite_invite_description')}
      modalWidth="min(1040px, 100%)"
      stickyHeader
      viewportSpacing
      onClose={onClose}
    >
      <ModalBody>
        <FormCore ref={formRef} formItems={formItems} register={initialValues} disabled={saving} />
        {!editingInvite ? <CompanyHint>{t('lite_invite_default_content_help')}</CompanyHint> : null}
        <CompanyHint>{t(form.access_duration_days == null ? 'lite_invite_legacy' : 'lite_invite_duration_help')}</CompanyHint>
        <ContentPresetSelector
          groups={contentGroups}
          loading={contentGroupsLoading}
          error={contentGroupsError}
          selectedId={selectedContentGroupId}
          onChange={onContentGroupChange}
        />
        <ThemedComponent>
        <AdvancedOptions defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{t('lite_invite_advanced')}</AccordionSummary>
          <AccordionDetails>
            <FormFieldsGrid>
              <FormField>
                <CompanyAutocomplete
                  placeholder={t('staff_essentials_invites_field_company')}
                  options={companyOptions}
                  value={selectedCompany}
                  inputValue={companyQuery}
                  loading={companyLoading}
                  noOptionsText={t(companyQuery ? 'staff_essentials_invites_no_companies' : 'staff_essentials_invites_search_companies')}
                  onInputChange={setCompanyQuery}
                  onChange={handleCompanyChange}
                />
                {!companyLoading ? null : <CompanyHint>{t('staff_essentials_invites_loading_companies')}</CompanyHint>}
                {!companyLoading && form.company_name && !form.company_id
                  ? <CompanyHint>{form.company_name}</CompanyHint>
                  : null}
              </FormField>
              {form.access_duration_days == null ? (
                <Input
                  placeholder={t('staff_essentials_invites_field_expires_at')}
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={event => setForm(current => ({ ...current, expires_at: event.target.value }))}
                />
              ) : null}
              <Check label={t('staff_essentials_invites_active')} checked={form.is_active}
                onChange={value => setForm(current => ({ ...current, is_active: value }))} />
            </FormFieldsGrid>
            <ContentLockSelector
              catalog={contentCatalog}
              types={contentCatalogTypes}
              loading={contentCatalogLoading}
              error={contentCatalogError}
              query={contentQuery}
              type={contentType}
              selected={form.allowed_contents}
              setQuery={setContentQuery}
              setType={setContentType}
              onChange={allowedContents => setForm(current => ({ ...current, content_selection_changed: true, content_group_ids: [], allowed_contents: allowedContents }))}
            />
          </AccordionDetails>
        </AdvancedOptions>
        </ThemedComponent>
        <FormActions>
          <Button outline color="blue" width="fit-content" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button color="blue" width="fit-content" loading={saving} disabled={saving || (!editingInvite && (contentCatalogLoading || Boolean(contentCatalogError)))} onClick={save}>
            {t('save')}
          </Button>
        </FormActions>
      </ModalBody>
    </ModalWrapper>
  )
}
